import { NewPassword } from '../../components/NewPassword';
import { SignInLink } from '../../components/SignInLink';
import { alreadyExists, errorText } from '../../errorText';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { verifyAccountSchema, verifyWithPasswordSchema } from './schema';
import { Flex, Button, Text, TextField, Callout, Link } from '@dodilio/ui-design-lib';
import { CreateAccountRequest } from '../../types/register';
import { z } from 'zod';
import { auth, hasOwnSession, ownSession } from '../../auth';
import { useAtom } from 'jotai';
import { createAccountAtom } from '../../state';
import { useNavigate } from 'react-router-dom';
import { ServiceRegister } from '../../api';
import { forgetSignupEmail, signupEmail } from '../../signupEmail';
import { ResendCode } from '../../components/ResendCode';
import { keycloakLink } from '../../signInLink';
import { followSignInLink } from '../../followSignInLink';

type FormData = z.infer<typeof verifyWithPasswordSchema>;

export const VerifyAccountForm = () => {
    const navigate = useNavigate();
    const [accountInfo, setAccountInfo] = useAtom(createAccountAtom);
    // A refresh loses the account step's state; the email is kept for the tab, the password never is.
    const [keptEmail] = useState(signupEmail);
    const email = accountInfo?.email || keptEmail;
    const askPassword = !accountInfo?.password;
    const [exists, setExists] = useState(false);
    const [notice, setNotice] = useState('');
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState<string>('');

    const sendVerification = async (): Promise<boolean> => {
        if (!email) return false;
        setNotice('');
        try {
            await ServiceRegister.createAccountInit({ email });
            setStatus('idle');
            clearErrors();
            setNotice(`Code sent. Check ${email} for the new one.`);
            return true;
        } catch (e: unknown) {
            setStatus('error');
            setExists(alreadyExists(e));
            setMessage(errorText(e, 'account'));
            return false;
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        clearErrors,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: zodResolver((askPassword ? verifyWithPasswordSchema : verifyAccountSchema) as any),
        // Checked on Verify, not on blur: an error appearing on blur pushed Resend and Back
        // down just as they were clicked.
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const onSubmit = async (data: FormData) => {
        if (!email) return navigate('/');
        const payload: CreateAccountRequest = {
            email,
            password: accountInfo?.password || data.password,
            verificationCode: data.verificationCode,
        };
        setStatus('idle');
        setNotice('');
        setExists(false);
        try {
            const responseLoad = await ServiceRegister.createAccount(payload);
            // iam's one-time Keycloak link: a real session for every app, no screen.
            const link = keycloakLink(responseLoad?.signInUrl);
            const { accessToken, refreshToken, idToken } = responseLoad?.tokens || {};
            if (accessToken && refreshToken && idToken) {
                const ok = await auth.initWithExternalTokens({
                    token: accessToken,
                    refreshToken: refreshToken,
                    idToken: idToken,
                });
                if (ok) {
                    ownSession();
                    forgetSignupEmail();
                    // Keep the email (the redirect below must not see an empty step), drop the password.
                    setAccountInfo({ email, password: '' });
                    setStatus('success');
                    setMessage('Account created! Setting up your organization…');
                    if (link) { void followSignInLink(link, email); return; }
                    // Replace: Back must not return to a code that's been spent.
                    navigate('/signup/org', { replace: true });
                    return;
                }
            }
            // No tokens to keep here, but Keycloak can still sign them in: it comes back to the org step.
            if (link) {
                ownSession();
                forgetSignupEmail();
                setAccountInfo({ email, password: '' });
                void followSignInLink(link, email);
                return;
            }
            // The account exists now, only the hand-over of its sign-in didn't work: a retry would
            // say "already exists", so send them to sign in.
            forgetSignupEmail();
            setStatus('error');
            setExists(true);
            setMessage('Your account was created, but we couldn\'t sign you in. Sign in to continue.');
        } catch (e) {
            const incoming_message = errorText(e, 'verify');
            setExists(alreadyExists(e));
            setStatus('error');
            setMessage(incoming_message);
        }
    };

    useEffect(() => {
        // The account exists already (Back after it was made): carry on, never the code again.
        if (hasOwnSession()) { navigate('/signup/org', { replace: true }); return; }
        if (!email) navigate('/');
    }, [email, navigate]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {status !== 'idle' && (
                <Callout.Root
                    mb="4"
                    size="1"
                    className="api-banner"
                    variant="soft"
                    color={status === 'success' ? 'green' : 'tomato'}
                    role={status === 'error' ? 'alert' : undefined}
                >
                    <Callout.Text>
                        {message}
                        {status === 'error' && exists && <>{' '}<SignInLink email={email} /></>}
                    </Callout.Text>
                </Callout.Root>
            )}
            {notice && status !== 'success' && (
                <Callout.Root mb="4" size="1" variant="soft" color="green" role="status">
                    <Callout.Text>{notice}</Callout.Text>
                </Callout.Root>
            )}
            {status !== 'success' && (
                <Flex direction="column" gap="3">
                    {/* Verification Code */}
                    <TextField.Root
                        type="text"
                        inputMode="numeric"
                        autoFocus={true}
                        {...register('verificationCode', {
                            // A pasted code ("123 456", "Code: 123456") is the six digits in it;
                            // the sixth one sends it, unless a password is still to come.
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                                if (digits !== e.target.value) setValue('verificationCode', digits);
                                if (digits.length === 6 && !askPassword && !isSubmitting) void handleSubmit(onSubmit)();
                            },
                        })}
                        maxLength={32}
                        size="2"
                        color={errors.verificationCode ? 'tomato' : 'gray'}
                        variant="surface"
                        placeholder="XXXXXX"
                        aria-label="Verification code"
                        autoComplete="one-time-code"
                    >
                        <TextField.Slot className="fieldLabel">
                            <Text size="1">Code</Text>
                        </TextField.Slot>
                    </TextField.Root>
                    {errors.verificationCode && (
                        <Text size="1" className="field-error" role="alert">{errors.verificationCode.message}</Text>
                    )}

                    {askPassword && (<>
                        <Text size="2" color="gray">
                            We sent the code to {email}. Your password isn&apos;t kept when the page reloads, so choose it again.
                        </Text>
                        <NewPassword field={register('password')} value={watch('password') ?? ''} error={errors.password?.message} />
                    </>)}

                    <Flex mt="6" width="100%" direction="row" gap="3" align="center" justify="between">
                        <Flex gap="5" align="center">
                            {/* The account step keeps what they typed (the email is in the step's state). */}
                            <Link href="/" underline="always" size="2" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                                Wrong email?
                            </Link>
                            {/* Just sent from the account step: the countdown shows from the start. */}
                            <ResendCode onResend={sendVerification} coolingFrom={accountInfo?.password ? 60 : 0} />
                        </Flex>
                        <Button
                            radius="full"
                            type="submit"
                            size="2"
                            variant="classic"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                        >
                            Verify
                        </Button>
                    </Flex>
                </Flex>
            )}
        </form>
    );
};
